import { useRef, useMemo } from 'react';

const DEBOUNCE_MS = 1000;
enum TimerType {
  Init = 'Init',
  Update = 'Update',
}

export function useDebouncedCallback<T extends [...any]>(
  callback: (...args: T) => any,
  baseDeps?: unknown[],
  baseDelay?: number,
) {
  const deps = baseDeps ?? [];
  const delay = baseDelay ?? DEBOUNCE_MS;
  const timerId = useRef<number>();

  const clearDebouncedCall = () => {
    window.clearTimeout(timerId.current);
    timerId.current = undefined;
  };

  const debouncedCallbackWrapper = useMemo(() => {
    const debouncedCallback: typeof callback = (...args) => {
      const setTimer = (type: TimerType) => {
        const isNoDelayCb = type === TimerType.Init;
        if (isNoDelayCb) {
          callback(...args);
        }
        return window.setTimeout(() => {
          if (!isNoDelayCb) {
            callback(...args);
          }
          timerId.current = undefined;
        }, delay);
      };

      if (timerId.current) {
        window.clearTimeout(timerId.current);
        timerId.current = setTimer(TimerType.Update);
        return;
      }

      // Первый запрос делаем без задержки
      timerId.current = setTimer(TimerType.Init);
    };

    return debouncedCallback;
  }, deps);

  return {
    clearDebouncedCall,
    debouncedCallbackWrapper,
  };
}
